import { BehaviorSubject, Observable } from 'rxjs';

const canBroadcast =
  typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined';

const createInstanceId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export type BroadcastBehaviorSubject<T> = {
  asObservable: () => Observable<T>;
  next: (value: T) => void;
  getValue: () => T;
  destroy: () => void;
  instanceId: string;
};

export const createBroadcastBehaviorSubject = <T>(
  channelName: string,
  initialValue: T,
  isEnabled?: () => boolean
): BroadcastBehaviorSubject<T> => {
  const instanceId = createInstanceId();
  const subject = new BehaviorSubject<T>(initialValue);
  const channel = canBroadcast ? new BroadcastChannel(channelName) : null;

  const isBroadcastEnabled = () => (isEnabled ? isEnabled() : true);

  if (channel) {
    channel.addEventListener('message', (event: MessageEvent) => {
      const { value, instanceId: senderId } = (event.data || {}) as {
        value: T;
        instanceId?: string;
      };
      if (senderId && senderId === instanceId) return;
      if (!isBroadcastEnabled()) return;
      subject.next(value);
    });
  }

  const next = (value: T) => {
    subject.next(value);
    channel?.postMessage({ value, instanceId });
  };

  const destroy = () => {
    channel?.close();
  };

  return {
    asObservable: () => subject.asObservable(),
    next,
    getValue: () => subject.getValue(),
    destroy,
    instanceId
  };
};
